import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { useQuery } from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { Button } from "@tellescope/ui";
import { useMemo, useRef, useState } from "react";
import { StripePaymentForm } from "./StripePaymentForm";
import { SelectedProduct, StripeProductSelection } from "./StripeProductSelection";
import { productIsVisible } from "./productIsVisible";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { StripeFieldProps } from "./StripeField";
import { resolveErrorMessage } from "../../utils";
import { emitFormAnalyticsEvent } from "../../hooks/emit-form-analytics-event";
import { useStripeProducts } from "./useStripeProducts";

export interface StripeFieldContentProps extends Omit<StripeFieldProps, "title"> {}

export const StripeFieldContent = ({
  id,
  value,
  onChange,
  enduserId,
  form,
  formResponses,
  options,
  setCustomerId,
}: StripeFieldProps) => {
  const { t } = useTranslation("forms");
  const [paymentElementIsReady, setPaymentElementIsReady] = useState(false);
  const purchaseReportedRef = useRef(false);
  const [storedSelection, setStoredSelection] = useState<SelectedProduct[]>([]);
  const enduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: enduserId,
    responseSideEffect: (_request, enduser) => {
      if (enduser.stripeCustomerId) setCustomerId?.(enduser.stripeCustomerId);
    },
  });
  const selectedProducts = useMemo(
    () =>
      storedSelection.filter(({ id: productId }) =>
        productIsVisible(productId, {
          options: options ?? {},
          formResponses,
          form,
          dateOfBirth: enduserQuery.data?.dateOfBirth,
          gender: enduserQuery.data?.gender,
          state: enduserQuery.data?.state,
        })
      ),
    [storedSelection, options, formResponses, form, enduserQuery.data]
  );
  const selectedProductIds = useMemo(() => selectedProducts.map(({ id }) => id), [selectedProducts]);
  const showProductSelection = options?.stripeProductSelectionMode && (options.productIds?.length ?? 0) > 1;
  const [showPayment, setShowPayment] = useState(!showProductSelection);
  const { isTerminal: productsAreTerminal } = useStripeProducts({
    productIds: options?.productIds ?? [],
    enduserId,
    formResponses,
    form,
    options: options ?? {},
  });
  const stripeDetailsQuery = useTellescopeQuery({
    model: "form_responses",
    method: "stripe_details",
    request: {
      fieldId: id,
      enduserId,
      selectedProductIds,
    },
    // Each call creates a payment intent, so hold off until the enduser has committed to a
    // selection — and never re-fetch for a field that is already paid.
    enabled: !value && (!showProductSelection || showPayment),
    responseSideEffect: (_request, details) => {
      if (details.customerId) setCustomerId?.(details.customerId);
    },
  });

  const stripeQuery = useQuery({
    queryKey: [
      "stripe",
      "loadStripe",
      {
        publishableKey: stripeDetailsQuery.data?.publishableKey,
        stripeAccount: stripeDetailsQuery.data?.stripeAccount,
      },
    ],
    queryFn: async () => {
      const res = await loadStripe(stripeDetailsQuery.data?.publishableKey ?? "", {
        stripeAccount: stripeDetailsQuery.data?.stripeAccount,
      });

      return res;
    },
    enabled: !!stripeDetailsQuery.data?.publishableKey,
  });

  if (value) {
    return (
      <div className="flex items-center justify-center min-h-3 gap-1">
        <CheckCircle2 color="white" fill="green" />
        <p className="text-sm">
          {options?.chargeImmediately ? t("Your purchase was successful") : t("Your payment details have been saved!")}
        </p>
      </div>
    );
  }

  if (showProductSelection && !showPayment) {
    return (
      <div className="space-y-1">
        <StripeProductSelection
          productIds={options.productIds ?? []}
          enduserId={enduserId}
          formResponses={formResponses}
          form={form}
          options={options}
          selectedProducts={selectedProducts}
          onSelectionChange={setStoredSelection}
        />
        {/* A terminal step can never be satisfied, so the button would be permanently dead. */}
        {!productsAreTerminal && (
          <Button
            onClick={() => setShowPayment(true)}
            disabled={!selectedProductIds.length}
            loading={stripeDetailsQuery.isLoading || stripeQuery.isLoading}
          >
            {t("Continue to Payment")}
          </Button>
        )}
      </div>
    );
  }

  // `fallbackAnswerText` is the recorded response value, so it stays untranslated — translating it
  // would store a different answer per enduser locale.
  const completePayment = (fallbackAnswerText: string) => {
    // A server-reported amount is what distinguishes a real charge from a saved card or a
    // misconfigured immediate-charge field with no products, so it gates the purchase event
    // alongside `chargeImmediately`. Reported once per mount: the embedded checkout can re-deliver
    // onComplete, and a double-count would corrupt the revenue these tags exist to measure.
    const amount = stripeDetailsQuery.data?.amount;
    if (options?.chargeImmediately && typeof amount === "number" && !purchaseReportedRef.current) {
      purchaseReportedRef.current = true;
      // NB: /100 assumes a 2-decimal currency, as legacy did.
      const dollars = Number((amount / 100).toFixed(2));
      emitFormAnalyticsEvent({
        event: "form_purchase",
        productIds: options.productIds ?? [],
        fieldId: id,
        value: dollars,
        currency: (stripeDetailsQuery.data?.currency || "USD").toUpperCase(),
        // Populated by the server only when the form opted in via `sendProductNamesToGTM`, so no
        // product-name policy is decided here.
        purchaseName: stripeDetailsQuery.data?.gtmPurchaseName || "",
        purchaseCost: dollars,
      });
    }

    onChange(stripeDetailsQuery.data?.answerText || fallbackAnswerText);
  };

  if (stripeDetailsQuery.isError) {
    return (
      <p className="text-sm text-destructive">
        {resolveErrorMessage(stripeDetailsQuery.error, t("An error occurred loading payment details"))}
      </p>
    );
  }

  if (stripeDetailsQuery.data?.isCheckout && stripeQuery.data) {
    return (
      <EmbeddedCheckoutProvider
        stripe={stripeQuery.data}
        options={{
          clientSecret: stripeDetailsQuery.data.clientSecret,
          onComplete: () => completePayment("Completed checkout"),
        }}
      >
        {showProductSelection && (
          <div className="flex mt-2 mb-2">
            <Button onClick={() => setShowPayment(false)} variant="ghost">
              <ArrowLeft />
              {t("Back to Product Selection")}
            </Button>
          </div>
        )}
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    );
  }

  const cost = selectedProducts.reduce<number>((acc, { cost }) => acc + cost, 0);

  if (!stripeQuery.data || !stripeDetailsQuery.data?.clientSecret) return null;

  return (
    <Elements
      stripe={stripeQuery.data}
      options={{
        clientSecret: stripeDetailsQuery.data?.clientSecret,
      }}
    >
      <StripePaymentForm
        onReady={() => setPaymentElementIsReady(true)}
        onSuccess={() => completePayment("Saved card details")}
        businessName={stripeDetailsQuery.data?.businessName ?? ""}
        paymentElementIsReady={paymentElementIsReady}
        chargeImmediately={!!options?.chargeImmediately}
        cost={cost}
        customPriceMessage={options?.customPriceMessage}
      />
    </Elements>
  );
};
